import Decimal from 'decimal.js';
import {
  isSheetErrorCell,
  sheetCellText,
  type SheetCell,
} from '@erp/shared-utils';

/**
 * Cells of a supplier's input workbook, and the reads every layout mapping
 * shares: text, exact decimals, AS400 dates.
 */

/** A cell: text, number, boolean, an Excel error cell (`#N/A`), or blank. */
export type ResultsCell = SheetCell;

/** The first sheet of a supplier workbook: its header row and data rows. */
export interface ResultsSheet {
  headers: string[];
  rows: ResultsCell[][];
  /** The workbook counts date serials from 1904 (older Mac Excel files). */
  date1904?: boolean;
  /** Excel row number of the header row; data follows it. Defaults to 1. */
  headerRowNumber?: number;
  /** Excel row number of each entry of `rows`, when rows are not contiguous. */
  rowNumbers?: number[];
}

export const UPLOAD_WIDTH = 82;

/** Zero-based index of an upload column letter (`A` → 0, `AS` → 44). */
export function columnIndex(letters: string): number {
  let index = 0;
  for (const letter of letters) {
    index = index * 26 + (letter.charCodeAt(0) - 64);
  }
  return index - 1;
}

export const isErrorCell = isSheetErrorCell;

/** A cell as trimmed text. Identifiers stay text: zeros are never dropped. */
export const cellText = sheetCellText;

export function isBlankRow(row: ResultsCell[]): boolean {
  return row.every((cell) => cellText(cell) === '');
}

/**
 * A plain decimal as typed in a sheet: sign, digits, one point. Decimal.js
 * would also take `NaN`, `Infinity`, `0x1F` and `1e3`, none of which belong in
 * an AS400 upload.
 */
const PLAIN_DECIMAL = /^[-+]?(\d+\.?\d*|\.\d+)$/;

export function cellDecimal(cell: ResultsCell): Decimal | null {
  if (typeof cell === 'number') {
    return Number.isFinite(cell) ? new Decimal(cell) : null;
  }
  const raw = cellText(cell);
  return PLAIN_DECIMAL.test(raw) ? new Decimal(raw) : null;
}

/**
 * Excel `ROUND(x, n)`: half away from zero, written with exactly n decimals.
 * With no `n`, the value as Excel shows an unrounded result: 15 significant
 * digits, no trailing zeros (`0.5`, not `0.50`).
 */
export function roundTo(value: Decimal, decimals: number | null): string {
  if (decimals === null) return value.toSignificantDigits(15).toFixed();
  return value.toFixed(decimals, Decimal.ROUND_HALF_UP);
}

const EXCEL_EPOCH_1900_UTC = Date.UTC(1899, 11, 30);
const EXCEL_EPOCH_1904_UTC = Date.UTC(1904, 0, 1);
const MS_PER_DAY = 86_400_000;

/** AS400 dates are `YYMMDD`. Workbooks carry `YYYY-MM-DD` text or a date serial. */
/** The last day Excel can hold, 9999-12-31, as a 1900-system serial. */
const MAX_EXCEL_SERIAL = 2_958_465;

function yymmdd(date: Date): string {
  return [
    date.getUTCFullYear() % 100,
    date.getUTCMonth() + 1,
    date.getUTCDate(),
  ]
    .map((part) => String(part).padStart(2, '0'))
    .join('');
}

function serialDate(serial: number, date1904: boolean): Date | null {
  if (!Number.isInteger(serial) || serial < 1 || serial > MAX_EXCEL_SERIAL) {
    return null;
  }
  const epoch = date1904 ? EXCEL_EPOCH_1904_UTC : EXCEL_EPOCH_1900_UTC;
  return new Date(epoch + serial * MS_PER_DAY);
}

/** `YYYY-MM-DD` that names a real day: `2026-99-99` and `2026-02-30` do not. */
function isoDate(text: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (!match) return null;
  const [year, month, day] = [match[1], match[2], match[3]].map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCMonth() === month - 1 && date.getUTCDate() === day
    ? date
    : null;
}

/** A date cell as a UTC day: `YYYY-MM-DD` text or a date serial. */
export function cellDate(cell: ResultsCell, date1904: boolean): Date | null {
  return typeof cell === 'number'
    ? serialDate(cell, date1904)
    : isoDate(cellText(cell));
}

/** AS400 dates are `YYMMDD`. Workbooks carry `YYYY-MM-DD` text or a date serial. */
export function as400Date(cell: ResultsCell, date1904: boolean): string | null {
  const date = cellDate(cell, date1904);
  return date ? yymmdd(date) : null;
}
